import I18n from 'I18n';
import { useCallback, useState, useEffect } from 'react';
import { FORM_FIELD_TYPES } from 'MeetingsBase/constants/enums';
// @ts-expect-error untyped import
import { fromMoment, toMomentUTC } from 'UIComponents/core/SimpleDate';
import { List } from 'foundations-components/transitional/components/List';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import UISelectInput from 'UIComponents/input/UISelectInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { View } from 'foundations-components/components/View';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMAIL_FIELD = 'email';
const MINUS_SIGN = '\u2212';
const INVALID_NUM_CHARS_REGEX = new RegExp(`[^0-9.${MINUS_SIGN}-]`, 'g');
const INVALID_DASHES_REGEX = new RegExp(`(?!^-)-`, 'g');
const INVALID_MINUS_SIGN_REGEX = new RegExp(`(?!^${MINUS_SIGN})${MINUS_SIGN}`, 'g');
const WHITE_SPACE_REGEX = /\s/g;
function getEnumOptions(options) {
  return options.map(option => {
    return {
      text: option.label,
      value: option.value
    };
  });
}
export function BookingFormField({
  fieldName,
  setFormValue,
  fieldValue,
  validation,
  autocomplete = 'off',
  fieldType,
  type,
  isRequired,
  fieldLabel,
  fieldOptions,
  fieldIsCustom,
  placeholder,
  numberDisplayHint,
  disabled
}) {
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  useEffect(() => {
    if (fieldType === FORM_FIELD_TYPES.CHECKBOX) {
      if (fieldValue == null) {
        setFormValue(fieldName, []);
      } else if (typeof fieldValue === 'string') {
        setFormValue(fieldName, fieldValue.split(';'));
      }
    }
    if (fieldType === FORM_FIELD_TYPES.DATE && fieldValue && typeof fieldValue === 'string') {
      // If a fieldValue for a date field is passed as a query param, it has to be in a YYYY-MM-DD string
      const utcFieldValue = I18n.moment.utc(fieldValue);
      const timestampFromFieldValue = isNaN(utcFieldValue.valueOf()) ? null : toMomentUTC(fromMoment(utcFieldValue)).valueOf();
      if (timestampFromFieldValue) {
        setFormValue(fieldName, timestampFromFieldValue);
      }
    }
  }, [fieldName, fieldType, fieldValue, setFormValue]);
  const isNameField = ['firstName', 'lastName'].includes(fieldName);
  const onFieldBlur = useCallback(() => {
    setHasBeenBlurred(true);
  }, []);
  const handleUnformattedNumberBlur = useCallback(e => {
    // Ignore extraneous characters to mimic original UINumberInput behavior
    const validValue = e.target.value.replace(INVALID_NUM_CHARS_REGEX, '').replace(INVALID_DASHES_REGEX, '').replace(INVALID_MINUS_SIGN_REGEX, '');
    setFormValue(fieldName, validValue);
    onFieldBlur();
  }, [fieldName, onFieldBlur, setFormValue]);
  const handlePhoneNumberFieldBlur = useCallback(e => {
    let validValue = e.target.value;
    validValue = validValue.replace(WHITE_SPACE_REGEX, '');
    setFormValue(fieldName, validValue);
    onFieldBlur();
  }, [fieldName, onFieldBlur, setFormValue]);
  const handleEnumValueChange = useCallback(value => {
    setFormValue(fieldName, value);
  }, [fieldName, setFormValue]);
  const handleBooleanCheckboxValueChange = useCallback(e => {
    setFormValue(fieldName, e.target.checked);
  }, [fieldName, setFormValue]);
  const handleValueChange = useCallback(e => {
    let value = e.currentTarget.value;
    if (fieldName === EMAIL_FIELD) {
      value = value.trim();
    }
    setFormValue(fieldName, value);
  }, [fieldName, setFormValue]);
  const handleDateValueChange = useCallback(e => {
    setFormValue(fieldName, toMomentUTC(e.target.value).valueOf());
  }, [fieldName, setFormValue]);
  const renderTextField = useCallback(FormFieldComponent => {
    // Text fields only deal with string values
    const stringCastedFieldValue = fieldValue;
    const onBlur = e => {
      if (fieldType === FORM_FIELD_TYPES.PHONE_NUMBER) {
        return handlePhoneNumberFieldBlur(e);
      }
      return onFieldBlur();
    };
    return /*#__PURE__*/_jsx(FormFieldComponent, {
      dir: "auto",
      maxInlineSize: "100%",
      textAlign: "start",
      autoComplete: autocomplete,
      type: "text",
      name: fieldName,
      "data-test-id": `confirm-${fieldName}-field`,
      value: stringCastedFieldValue || '',
      onChange: handleValueChange,
      onBlur: onBlur,
      placeholder: placeholder,
      disabled: disabled
    });
  }, [autocomplete, disabled, fieldName, fieldType, fieldValue, handlePhoneNumberFieldBlur, handleValueChange, onFieldBlur, placeholder]);
  const renderTextInputField = useCallback(() => {
    return renderTextField(TextInput);
  }, [renderTextField]);
  const renderTextAreaField = useCallback(() => {
    return renderTextField(TextArea);
  }, [renderTextField]);
  const renderEnumType = useCallback(renderFunction => {
    return /*#__PURE__*/_jsx(List, {
      wrapChildren: true,
      children: fieldOptions && fieldOptions.map(option => {
        if (option.value !== '') {
          return renderFunction(option);
        }
        return null;
      })
    });
  }, [fieldOptions]);
  const renderCheckbox = useCallback(() => {
    const castedCheckboxFieldValue = fieldValue;
    return /*#__PURE__*/_jsxs(Checkbox, {
      checked: castedCheckboxFieldValue,
      onChange: handleBooleanCheckboxValueChange,
      disabled: disabled,
      marginBlockStart: "600",
      children: [fieldLabel, isRequired && /*#__PURE__*/_jsx("span", {
        children: " * "
      })]
    });
  }, [disabled, fieldLabel, fieldValue, handleBooleanCheckboxValueChange, isRequired]);
  const renderRadioInput = useCallback(option => {
    return /*#__PURE__*/_jsx(RadioInput, {
      name: `${fieldName}-${option.value}`,
      checked: option.value === fieldValue,
      disabled: disabled,
      onChange: () => handleEnumValueChange(option.value),
      children: option.label
    }, `${fieldName}-${option.value}`);
  }, [disabled, fieldName, fieldValue, handleEnumValueChange]);
  const renderSelectField = useCallback(() => {
    const placeholderText = I18n.text('select') || placeholder;
    if (fieldType === FORM_FIELD_TYPES.CHECKBOX) {
      const castedCheckboxFieldValue = fieldValue;
      return /*#__PURE__*/_jsx(ToggleGroup, {
        name: fieldName,
        children: fieldOptions && fieldOptions.map(option => {
          const onChange = ({
            target: {
              checked
            }
          }) => {
            if (checked) {
              setFormValue(fieldName, [...castedCheckboxFieldValue, option.value]);
            } else {
              setFormValue(fieldName, (castedCheckboxFieldValue || []).filter(value => value !== option.value));
            }
          };
          return {
            onChange,
            checked: castedCheckboxFieldValue.includes(option.value),
            key: option.value,
            label: option.label
          };
        }).map(option => {
          const {
            key,
            label,
            onChange,
            checked
          } = option;
          return /*#__PURE__*/_jsx(Checkbox, {
            disabled: disabled,
            onChange: onChange,
            checked: checked,
            children: label
          }, key);
        })
      });
    } else {
      // Do not switch to UISelect despite deprecation warning in UILibrary
      // See https://git.hubteam.com/HubSpot/MeetingsUI/pull/1446
      return /*#__PURE__*/_jsx(UISelectInput, {
        name: fieldName,
        onChange: handleValueChange,
        onBlur: onFieldBlur,
        options: getEnumOptions(fieldOptions || []),
        placeholder: placeholderText,
        value: fieldValue || undefined,
        disabled: disabled
      });
    }
  }, [disabled, fieldName, fieldOptions, fieldType, fieldValue, handleValueChange, onFieldBlur, placeholder, setFormValue]);
  const renderEnumerationFields = useCallback(() => {
    switch (fieldType) {
      case FORM_FIELD_TYPES.BOOLEAN_CHECKBOX:
        return renderCheckbox();
      case FORM_FIELD_TYPES.RADIO:
        return renderEnumType(renderRadioInput);
      default:
        return renderSelectField();
    }
  }, [fieldType, renderCheckbox, renderEnumType, renderRadioInput, renderSelectField]);
  const renderUnformattedNumberField = useCallback(() => {
    const castedUnformattedNumberFieldValue = fieldValue;
    return /*#__PURE__*/_jsx(TextInput, {
      name: fieldName,
      onChange: handleValueChange,
      onBlur: handleUnformattedNumberBlur,
      value: castedUnformattedNumberFieldValue,
      disabled: disabled
    });
  }, [disabled, fieldName, fieldValue, handleUnformattedNumberBlur, handleValueChange]);
  const renderNumberField = useCallback(() => {
    if (numberDisplayHint === 'unformatted') {
      return renderUnformattedNumberField();
    } else {
      const parsedFieldValue = typeof fieldValue === 'string' ? parseInt(fieldValue, 10) : fieldValue;
      const inputValue = isNaN(parsedFieldValue) ? 0 : parsedFieldValue;
      return /*#__PURE__*/_jsx(NumberInput, {
        name: fieldName,
        onChange: handleValueChange,
        onBlur: onFieldBlur,
        value: inputValue,
        disabled: disabled
      });
    }
  }, [disabled, fieldName, fieldValue, handleValueChange, numberDisplayHint, onFieldBlur, renderUnformattedNumberField]);
  const renderDateField = useCallback(() => {
    let dateValue = null;
    let parsedFieldValue = null;
    if (fieldValue && typeof fieldValue === 'string') {
      parsedFieldValue = parseInt(fieldValue, 10);
    } else if (fieldValue && typeof fieldValue === 'number') {
      parsedFieldValue = fieldValue;
    }
    if (parsedFieldValue) {
      const utcFieldValue = I18n.moment.utc(parsedFieldValue);
      dateValue = isNaN(parsedFieldValue) ? null : fromMoment(utcFieldValue);
    }
    return /*#__PURE__*/_jsx(View, {
      inlineSize: "180px",
      children: /*#__PURE__*/_jsx(DateInput, {
        "data-test-id": "date-input",
        format: I18n.moment.localeData().longDateFormat('L') ? 'L' : 'YYYY-MM-DD',
        value: dateValue,
        onChange: handleDateValueChange,
        disabled: disabled
      })
    });
  }, [disabled, fieldValue, handleDateValueChange]);
  const renderInput = useCallback(() => {
    switch (true) {
      case type === FORM_FIELD_TYPES.ENUMERATION:
        return renderEnumerationFields();
      case type === FORM_FIELD_TYPES.NUMBER:
        return renderNumberField();
      case type === FORM_FIELD_TYPES.DATE:
        return renderDateField();
      case fieldIsCustom || fieldType === FORM_FIELD_TYPES.TEXTAREA:
        return renderTextAreaField();
      default:
        return renderTextInputField();
    }
  }, [fieldIsCustom, fieldType, renderDateField, renderEnumerationFields, renderNumberField, renderTextAreaField, renderTextInputField, type]);
  const hasError = Boolean(validation) && hasBeenBlurred;
  let validationMessage;
  if (hasError) {
    validationMessage = validation;
  }
  const isBooleanCheckbox = fieldType === FORM_FIELD_TYPES.BOOLEAN_CHECKBOX;
  return /*#__PURE__*/_jsx(FormControl, {
    "data-selenium-test": `booking-form-field-${fieldName}`,
    "data-test-id": "booking-form-field",
    label: !isBooleanCheckbox && fieldLabel,
    required: isRequired,
    error: hasError,
    validationMessage: /*#__PURE__*/_jsx("span", {
      "data-test-id": `booking-form-field-${fieldName}-validation-message`,
      children: validationMessage
    }),
    onBlur: onFieldBlur,
    marginBlockStart: !isNameField ? '600' : undefined,
    children: renderInput()
  });
}
export default BookingFormField;